import { useQuery, useQueryClient } from "@tanstack/react-query";
import { requestURLs } from "../../Environment";
import { QUERY_KEYS } from "./querykeys";
import {
  AxiosResponseAndError,
  ResponseType,
} from "../types/axiosResponseType";
import ApiRequest from "../ApiRequest";
import {
  OverAllTrackEnumValue,
  ProjectDefaultViewEnumValue,
  ProjectStatusEnumValue,
  ScheduleAndBudgetTrend,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import { UserOrganisationType } from "./useCurrentUserQuery";
import { Task } from "../mutation/useTaskCreateMutation";
import { useEffect } from "react";

export type Project = {
  spi?:number;
  cpi?: number;
  overallTrack: keyof typeof OverAllTrackEnumValue;
  projectId: string;
  assignedUsers: AssignedUsers[];
  organisationId: string;
  projectName: string;
  projectDescription: string;
  startDate: string;
  estimatedEndDate: string;
  status: keyof typeof ProjectStatusEnumValue;
  defaultView: keyof typeof ProjectDefaultViewEnumValue;
  timeTrack: string | null;
  budgetTrack: string | null;
  estimatedBudget: string;
  actualCost: string | null;
  progressionPercentage: number | string | null;
  createdAt: string;
  updatedAt: string;
  createdByUser: CreatedByUser;
  createdByUserId:string
  currency: string;
  projectManagerInfo: { user: CreatedByUser }[];
  scheduleTrend: keyof typeof ScheduleAndBudgetTrend;
  budgetTrend: keyof typeof ScheduleAndBudgetTrend;
  consumedBudget: string;
  reCalculateBudget: string;
  actualEndDate: string;
  actualStartDate: string;
  estimatedDuration: number;
  actualDuration: number;
  isActive:boolean
  portfolio:Portfolio | null
  program:Program | null,
  reCalculateEndDate?:Date|null
  isMilestone?:boolean
  reCalculatedDuration:number|null
  tasks:Task[]
}


export interface AssignedUsers {
  projectAssignUsersId: string;
  projectId:            string;
  assginedToUserId:     string;
  projectRole:          string|null;
  projectJobTitle:      string|null;
  createdAt:            Date;
  updatedAt:            Date;
  user:                 User;
}

export interface User {
  userId:           string;
  email:            string;
  status:           string;
  firstName:        string|null;
  lastName:         string|null;
  timezone:         null;
  country:          null;
  avatarImg:        null;
  isVerified:       boolean;
  createdAt:        Date;
  updatedAt:        Date;
  userOrganisation: UserOrganisationType[];
}

export interface UserOrganisation {
  role: keyof typeof UserRoleEnumValue;
}
export interface CreatedByUser {
  firstName: string|null;
  lastName: string|null;
  email: string;
  avatarImg: null;
}
export interface Portfolio {
  portfolioId: string
  organisationId: string
  portfolioName: string
}
export interface Portfolio {
  portfolioId: string
  organisationId: string
  portfolioName: string
}
export interface Program {
  programId: string
  portfolioId: string
  programName: string
}

type ProjectApiResponse = ResponseType<Project[]>;

function useProjectQuery(enabled = true, query = {}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const key = event?.query?.queryKey?.[0];

      if (key === QUERY_KEYS.getProjects && event?.type === 'updated') {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getProjectsNocal]
        });
      }
    });

    return unsubscribe;
  }, [queryClient]);

  return useQuery<
    AxiosResponseAndError<ProjectApiResponse>["response"],
    AxiosResponseAndError<ProjectApiResponse>["error"],
    AxiosResponseAndError<ProjectApiResponse>["response"] // TQueryFnData
  >({
    queryKey: [QUERY_KEYS.getProjects, query],
    queryFn: async () => {
      return await ApiRequest.get(requestURLs.project, { params: query });
    },
    enabled,
    placeholderData: (previous) => previous,
  });
}



export default useProjectQuery;


